import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";

const SLOT_LABELS = {
  1: "Slot 1 (2-4 pm)",
  2: "Slot 2 (4:30-6:30 pm)",
};

const BuffetBookings = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [bookings, setBookings] = useState({});
  const [slotsInfo, setSlotsInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [moveDropdownId, setMoveDropdownId] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const [error, setError] = useState("");

  const setErrorWithTimeout = (msg) => {
    setError(msg);
    if (msg) setTimeout(() => setError(""), 3000);
  };

  const getNextSunday = () => {
    const now = new Date();
    const dow = now.getDay();
    const daysUntil = dow === 0 ? 0 : 7 - dow;
    const sunday = new Date(now);
    sunday.setDate(now.getDate() + daysUntil);

    const yyyy = sunday.getFullYear();
    const mm = String(sunday.getMonth() + 1).padStart(2, "0");
    const dd = String(sunday.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    setSelectedDate(getNextSunday());
  }, []);

  const fetchBookings = useCallback(async (date, isBackground = false) => {
    if (!isBackground) setLoading(true);

    try {
      const data = await adminApiRequest({
        script: "get-bookings",
        data: { booking_date: date },
      });

      if (data.success) {
        setBookings(data.bookings || {});
        setSlotsInfo(data.slots_info || {});
      } else {
        setErrorWithTimeout(data.message || "Failed to fetch data.");
      }
    } catch {
      setErrorWithTimeout("Failed to fetch bookings.");
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) fetchBookings(selectedDate);
  }, [selectedDate, fetchBookings]);

  useEffect(() => {
    if (!selectedDate) return;

    const interval = setInterval(() => fetchBookings(selectedDate, true), 5000);

    return () => clearInterval(interval);
  }, [selectedDate, fetchBookings]);

  const handleArrivedToggle = async (id, current) => {
    setActionLoading(true);

    try {
      const data = await adminApiRequest({
        script: "update-booking",
        data: { id, arrived: current ? 0 : 1 },
      });

      if (data.success) fetchBookings(selectedDate);
      else setErrorWithTimeout(data.message || "Update failed.");
    } catch {
      setErrorWithTimeout("Update error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setActionLoading(true);

    try {
      const data = await adminApiRequest({
        script: "delete-booking",
        data: { id },
      });

      if (data.success) fetchBookings(selectedDate);
      else setErrorWithTimeout(data.message || "Delete failed.");
    } catch {
      setErrorWithTimeout("Delete error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMove = async (id, fromSlot, toSlot) => {
    if (
      fromSlot === toSlot ||
      !slotsInfo[toSlot] ||
      slotsInfo[toSlot].remaining <= 0
    ) {
      setErrorWithTimeout("Invalid move or slot full.");
      return;
    }

    setActionLoading(true);

    try {
      const data = await adminApiRequest({
        script: "move-booking",
        data: { id, new_slot_id: toSlot },
      });

      if (data.success) {
        fetchBookings(selectedDate);
        setMoveDropdownId(null);
      } else {
        setErrorWithTimeout(data.message || "Move failed.");
      }
    } catch {
      setErrorWithTimeout("Move error.");
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmModal = (title, onConfirm, content = null) => {
    setModalConfig({ title, onConfirm, content });
  };

  const closeModal = () => {
    setModalConfig(null);
  };

  const isPast =
    selectedDate &&
    new Date(selectedDate) < new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="mb-6 flex flex-shrink-0 items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold text-slate-800">
          Buffet Bookings
        </h1>

        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-100 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {(loading || actionLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-800"></div>
        </div>
      )}

      {/* Slots */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex h-full min-w-max gap-6 lg:grid lg:min-w-0 lg:grid-cols-2">
          {Object.keys(SLOT_LABELS).map((slotId) => {
            const slotBookings = bookings[slotId] || [];
            const sorted = [...slotBookings].sort(
              (a, b) => a.arrived - b.arrived,
            );

            return (
              <div
                key={slotId}
                className="flex h-full min-w-[350px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md sm:w-[420px] lg:w-auto"
              >
                {/* Slot Header */}
                <div className="flex flex-shrink-0 items-center justify-between border-b bg-slate-900 px-5 py-3 text-white">
                  <h2 className="font-medium">{SLOT_LABELS[slotId]}</h2>
                  {slotsInfo[slotId] && (
                    <div className="flex gap-2 text-xs">
                      <span className="rounded-full bg-white/20 px-3 py-1">
                        {slotsInfo[slotId].taken} taken
                      </span>

                      {!isPast && (
                        <span className="rounded-full bg-white/20 px-3 py-1">
                          {slotsInfo[slotId].remaining} left
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Scrollable bookings */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {sorted.length ? (
                    sorted.map((b) => (
                      <div
                        key={b.id}
                        className={`rounded-xl border p-4 transition hover:shadow ${
                          b.arrived
                            ? "border-emerald-200 bg-emerald-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <div className="space-y-1 text-sm">
                          <p>
                            <span className="font-semibold">Name:</span>{" "}
                            {b.name}
                          </p>

                          <p className="flex gap-4">
                            <span>
                              <span className="font-semibold">Adults:</span>{" "}
                              {b.num_adults}
                            </span>

                            <span>
                              <span className="font-semibold">Children:</span>{" "}
                              {b.num_children}
                            </span>
                          </p>

                          <p>
                            <span className="font-semibold">Phone:</span>{" "}
                            {b.phone}
                          </p>

                          <p>
                            <span className="font-semibold">Email:</span>{" "}
                            {b.email}
                          </p>
                        </div>

                        <div className="flex gap-2 pt-3">
                          <button
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-40"
                            onClick={() =>
                              openConfirmModal("Toggle Arrival", () =>
                                handleArrivedToggle(b.id, b.arrived),
                              )
                            }
                            disabled={isPast}
                            title="Toggle Arrived"
                          >
                            <i className="fa fa-check text-sm"></i>
                          </button>

                          <button
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-40"
                            onClick={() =>
                              setMoveDropdownId(
                                moveDropdownId === b.id ? null : b.id,
                              )
                            }
                            disabled={isPast}
                            title="Move Booking"
                          >
                            <i className="fa fa-arrows-alt-h text-sm"></i>
                          </button>

                          <button
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-600 text-white shadow-sm transition hover:bg-red-700 disabled:opacity-40"
                            onClick={() =>
                              openConfirmModal("Delete Booking", () =>
                                handleDelete(b.id),
                              )
                            }
                            disabled={isPast}
                            title="Delete Booking"
                          >
                            <i className="fa fa-trash text-sm"></i>
                          </button>
                        </div>

                        {moveDropdownId === b.id && !isPast && (
                          <select
                            className="mt-3 w-full rounded-lg border border-slate-200 p-2 text-sm"
                            defaultValue=""
                            onChange={(e) =>
                              handleMove(
                                b.id,
                                parseInt(slotId, 10),
                                parseInt(e.target.value, 10),
                              )
                            }
                          >
                            <option value="" disabled>
                              Move to...
                            </option>

                            {Object.keys(SLOT_LABELS)
                              .filter((s) => s !== slotId)
                              .map((s) => (
                                <option
                                  key={s}
                                  value={s}
                                  disabled={slotsInfo[s]?.remaining <= 0}
                                >
                                  {SLOT_LABELS[s]}
                                  {slotsInfo[s]?.remaining <= 0
                                    ? " (Full)"
                                    : ""}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-400">
                      No bookings
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalConfig && (
        <Modal
          title={modalConfig.title}
          onClose={closeModal}
          onSubmit={(e) => {
            e.preventDefault();
            modalConfig.onConfirm();
            closeModal();
          }}
          submitButtonText="Confirm"
          showCancel
          showSubmitButton
        >
          {modalConfig.content}
        </Modal>
      )}
    </div>
  );
};

export default BuffetBookings;
